import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
	Bug,
	Check,
	Link2,
	Lightbulb,
	MessageSquare,
	MousePointerClick,
	Palette,
	Send,
	Sparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BrandMentionsLandingPreview } from '@/components/landing/BrandMentionsLandingPreview'
import { HeroCapabilityAnimation } from '@/components/landing/HeroCapabilityAnimation'
import { HeroInboxPreview } from '@/components/landing/HeroInboxPreview'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import type { WidgetConfig } from '@/components/widget-builder/WidgetPreview'

const WidgetPreview = lazy(() =>
	import('@/components/widget-builder/WidgetPreview').then((module) => ({
		default: module.WidgetPreview,
	}))
)

const EMBED_MODES = ['inline', 'floating', 'trigger'] as const
type EmbedMode = (typeof EMBED_MODES)[number]

function LandingEmbedWidget({
	config,
	showFab,
	autoOpen,
	mode,
}: {
	config: WidgetConfig
	showFab: boolean
	autoOpen: boolean
	mode: EmbedMode
}) {
	useEffect(() => {
		window.dispatchEvent(new Event('feedbask:close-widget-preview'))
		if (!autoOpen) return
		const timer = window.setTimeout(() => {
			window.dispatchEvent(new Event('feedbask:open-widget-preview'))
		}, 800)
		return () => window.clearTimeout(timer)
	}, [autoOpen, mode])

	return <WidgetPreview config={config} showManualTrigger={showFab} />
}

const LANDING_WIDGET_PRIMARY = '#6547D9'

function useInView(threshold = 0.35) {
	const ref = useRef<HTMLDivElement>(null)
	const [inView, setInView] = useState(false)
	useEffect(() => {
		const el = ref.current
		if (!el) return
		const observer = new IntersectionObserver(([entry]) => {
			setInView(entry.isIntersecting)
		}, { threshold })
		observer.observe(el)
		return () => observer.disconnect()
	}, [threshold])
	return { ref, inView }
}

function useLoop(length: number, ms: number, enabled: boolean, reducedMotion: boolean) {
	const [step, setStep] = useState(0)
	useEffect(() => {
		if (reducedMotion || !enabled) return
		const timer = window.setInterval(() => {
			setStep((current) => (current + 1) % length)
		}, ms)
		return () => window.clearInterval(timer)
	}, [enabled, length, ms, reducedMotion])
	return reducedMotion ? length - 1 : step
}

function AnimFrame({ children }: { children: ReactNode }) {
	return (
		<div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
			{children}
		</div>
	)
}

export function AiDraftBenefitAnimation() {
	const { t } = useTranslation()
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const { ref, inView } = useInView()
	const step = useLoop(4, 1800, inView, reducedMotion)
	const draft = t('featuresAlternating.anim.draft')
	const [typed, setTyped] = useState('')

	useEffect(() => {
		if (reducedMotion) {
			setTyped(draft)
			return
		}
		if (step < 1) {
			setTyped('')
			return
		}
		if (step >= 2) {
			setTyped(draft)
			return
		}
		setTyped('')
		let index = 0
		const timer = window.setInterval(() => {
			index += 2
			setTyped(draft.slice(0, index))
			if (index >= draft.length) window.clearInterval(timer)
		}, 16)
		return () => window.clearInterval(timer)
	}, [draft, reducedMotion, step])

	return (
		<div ref={ref}>
			<AnimFrame>
				<div className="rounded-xl border border-border/70 bg-white p-4 shadow-sm">
					<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						{t('featuresAlternating.anim.question')}
					</p>
					<p className="mt-2 text-sm text-foreground">
						{t('featuresAlternating.anim.customerQuestion')}
					</p>
				</div>
				<div className="mt-4 rounded-xl border border-primary/20 bg-white p-4 shadow-sm">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							<Sparkles className="h-4 w-4 text-primary" />
							<p className="text-sm font-semibold">{t('featuresAlternating.anim.aiAssistant')}</p>
						</div>
						<span
							className={cn(
								'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
								step === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
							)}
						>
							{t('featuresAlternating.anim.generateDraft')}
						</span>
					</div>
					<div className="mt-3 min-h-[6.5rem] rounded-lg border border-primary/15 bg-primary/5 p-3 text-sm leading-relaxed text-foreground">
						<p className="text-xs font-medium text-muted-foreground">
							{t('featuresAlternating.anim.suggestedDraft')}
						</p>
						<p className="mt-2">
							{typed}
							{step === 1 && <span className="ml-0.5 inline-block h-4 w-px bg-primary align-middle" />}
						</p>
					</div>
					<div className="mt-3 flex justify-end">
						<span
							className={cn(
								'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
								step >= 3
									? 'bg-emerald-50 text-emerald-700'
									: 'bg-primary text-primary-foreground',
								step === 2 && 'scale-105 shadow-md'
							)}
						>
							{step >= 3 ? <Check className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
							{step >= 3
								? t('featuresAlternating.anim.sent')
								: t('featuresAlternating.anim.sendReply')}
						</span>
					</div>
				</div>
			</AnimFrame>
		</div>
	)
}

const CONTEXT_FIELDS = ['name', 'browser', 'os', 'page', 'plan'] as const

export function CustomerContextBenefitAnimation() {
	const { t } = useTranslation()
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const { ref, inView } = useInView(0.2)
	const [appeared, setAppeared] = useState(false)

	useEffect(() => {
		if (inView) setAppeared(true)
	}, [inView])

	return (
		<div ref={ref}>
			<div
				className={cn(
					!reducedMotion && (appeared ? 'animate-centralize-row' : 'opacity-0')
				)}
			>
				<AnimFrame>
					<div className="grid gap-4 sm:grid-cols-[1.1fr_0.9fr]">
						<div className="rounded-xl border border-border/70 bg-white p-4 shadow-sm">
							<p className="text-sm font-medium text-foreground">Maya Chen</p>
							<p className="mt-2 text-sm text-muted-foreground">
								{t('featuresAlternating.anim.contextMessage')}
							</p>
						</div>
						<div className="rounded-xl border border-border/70 bg-white p-4 shadow-sm">
							<p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
								{t('featuresAlternating.anim.reporterDetails')}
							</p>
							<div className="space-y-2.5">
								{CONTEXT_FIELDS.map((field) => (
									<div key={field} className="flex items-center justify-between gap-3">
										<span className="text-xs text-muted-foreground">
											{t(`featuresAlternating.anim.fields.${field}`)}
										</span>
										<span className="text-xs font-medium text-foreground">
											{t(`featuresAlternating.anim.values.${field}`)}
										</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</AnimFrame>
			</div>
		</div>
	)
}

export function CentralizedInboxBenefitAnimation() {
	return <HeroCapabilityAnimation />
}

export function DashboardBenefitAnimation() {
	return (
		<div className="relative h-[20rem] w-full overflow-hidden sm:h-[24rem] lg:h-[28rem]">
			<div className="absolute left-0 top-0 w-[153.85%] origin-top-left scale-[0.65]">
				<HeroInboxPreview />
			</div>
		</div>
	)
}

export function MentionsBenefitAnimation() {
	return <BrandMentionsLandingPreview />
}

function openLandingWidgetPreview() {
	window.dispatchEvent(new Event('feedbask:open-widget-preview'))
}

export function EmbedBenefitAnimation() {
	const { t, i18n } = useTranslation()
	const lang = i18n.language?.split('-')[0] || 'en'
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const { ref, inView } = useInView()
	const [step, setStep] = useState(0)
	const [armed, setArmed] = useState(false)
	const [fitScale, setFitScale] = useState(1)
	const pauseUntilRef = useRef(0)
	const fitRef = useRef<HTMLDivElement>(null)
	const mode: EmbedMode = EMBED_MODES[step]

	useEffect(() => {
		const holder = fitRef.current
		if (!holder) return
		const measure = () => {
			const panel = holder.querySelector<HTMLElement>('div[class*="w-[400px]"]')
			if (!panel) return
			const availableWidth = holder.clientWidth - 24
			const availableHeight = holder.clientHeight - 24
			const panelWidth = panel.offsetWidth || 400
			const panelHeight = panel.offsetHeight + 20
			const scale = Math.min(1, availableWidth / panelWidth, availableHeight / panelHeight)
			setFitScale(Number(scale.toFixed(3)))
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(holder)
		const timer = window.setInterval(measure, 400)
		return () => {
			observer.disconnect()
			window.clearInterval(timer)
		}
	}, [])

	useEffect(() => {
		if (reducedMotion || !inView) return
		const timer = window.setInterval(() => {
			if (Date.now() < pauseUntilRef.current) return
			setStep((current) => (current + 1) % EMBED_MODES.length)
		}, 3800)
		return () => window.clearInterval(timer)
	}, [inView, reducedMotion])

	useEffect(() => {
		setArmed(reducedMotion && mode !== 'floating')
		if (!inView || mode === 'floating' || reducedMotion) return
		const armTimer = window.setTimeout(() => setArmed(true), 700)
		return () => window.clearTimeout(armTimer)
	}, [inView, mode, reducedMotion])

	const config = useMemo<WidgetConfig>(
		() => ({
			program_id: '',
			name: 'Feedbask',
			enable_survey: true,
			enable_bug_reporting: true,
			enable_contact_messages: true,
			enable_feature_requests: true,
			enable_reviews: true,
			enable_live_chat: true,
			primary_feedback_type: 'nps',
			position: 'bottom-right',
			primaryColor: LANDING_WIDGET_PRIMARY,
			backgroundColor: '#FFFFFF',
			textColor: '#000000',
			feedbackButton: {
				show: true,
				text: 'Feedback',
				color: LANDING_WIDGET_PRIMARY,
				textColor: '#FFFFFF',
			},
			showBranding: true,
			showHeader: true,
			language: lang,
			trigger: { type: 'manual' },
			moduleNavigation: { enabled: false, defaultToFirstModule: false },
		}),
		[lang]
	)

	const selectMode = (index: number) => {
		setStep(index)
		pauseUntilRef.current = Number.POSITIVE_INFINITY
	}

	return (
		<div ref={ref}>
			<div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
				<div className="flex flex-wrap gap-2 border-b border-border/60 bg-white px-3 py-2.5">
					{EMBED_MODES.map((id, index) => {
						const Icon =
							id === 'inline' ? Link2 : id === 'trigger' ? MousePointerClick : MessageSquare
						return (
							<button
								key={id}
								type="button"
								onClick={() => selectMode(index)}
								className={cn(
									'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
									mode === id
										? 'bg-primary text-primary-foreground'
										: 'bg-muted text-muted-foreground hover:text-foreground'
								)}
							>
								<Icon className="h-3.5 w-3.5" />
								{t(`featuresAlternating.anim.embed.${id}`)}
							</button>
						)
					})}
				</div>
				<div className="flex h-8 items-center gap-2 border-b border-border/60 bg-slate-50 px-3">
					<span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
					<span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
					<span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
					<span className="ml-2 truncate text-xs text-muted-foreground">
						app.yoursite.com
					</span>
				</div>
				<div className="relative h-[21rem] overflow-hidden bg-white [transform:translateZ(0)] sm:h-[23rem]">
					<div className="p-5 sm:p-6">
						<div className="h-3 w-24 rounded bg-slate-200/80" />
						{mode === 'inline' && (
							<p className="mt-5 text-sm leading-relaxed text-foreground animate-centralize-row">
								{t('featuresAlternating.anim.embed.article')}{' '}
								<button
									type="button"
									onClick={openLandingWidgetPreview}
									className={cn(
										'font-semibold text-primary underline underline-offset-4 transition-transform',
										armed && 'scale-105'
									)}
								>
									{t('featuresAlternating.anim.embed.helpLink')}
								</button>
							</p>
						)}
						{mode === 'trigger' && (
							<div className="mt-5 space-y-3 animate-centralize-row">
								<pre className="overflow-x-auto rounded-lg bg-slate-950 px-3 py-2.5 font-mono text-xs text-emerald-300">
									{t('featuresAlternating.anim.embed.code')}
								</pre>
								<button
									type="button"
									onClick={openLandingWidgetPreview}
									className={cn(
										'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm transition-all',
										armed && 'scale-105 shadow-md'
									)}
									style={{ backgroundColor: LANDING_WIDGET_PRIMARY }}
								>
									<MousePointerClick className="h-4 w-4" />
									{t('featuresAlternating.anim.embed.openWidget')}
								</button>
							</div>
						)}
						{mode === 'floating' && (
							<>
								<div className="mt-4 h-5 w-2/3 rounded bg-slate-100" />
								<div className="mt-3 h-3 w-full rounded bg-slate-100" />
								<div className="mt-2 h-3 w-5/6 rounded bg-slate-100" />
								<div className="mt-2 h-3 w-3/4 rounded bg-slate-100" />
							</>
						)}
						{mode !== 'floating' && (
							<>
								<div className="mt-3 h-3 w-full rounded bg-slate-100" />
								<div className="mt-2 h-3 w-5/6 rounded bg-slate-100" />
							</>
						)}
					</div>
					<div
						ref={fitRef}
						className="absolute inset-0"
						style={{ transform: `scale(${fitScale})`, transformOrigin: 'bottom right' }}
					>
						<Suspense
							fallback={
								mode === 'floating' ? (
									<button
										type="button"
										tabIndex={-1}
										className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white shadow-lg"
										style={{ backgroundColor: LANDING_WIDGET_PRIMARY }}
									>
										<MessageSquare className="h-4 w-4" />
										Feedback
									</button>
								) : null
							}
						>
							<LandingEmbedWidget
								config={config}
								showFab={mode === 'floating'}
								autoOpen={inView && mode !== 'floating'}
								mode={mode}
							/>
						</Suspense>
					</div>
				</div>
			</div>
		</div>
	)
}

const THEMES = [
	{ id: 'violet', swatch: 'bg-violet-500', widget: 'bg-violet-600', panel: 'border-violet-200' },
	{ id: 'teal', swatch: 'bg-teal-500', widget: 'bg-teal-600', panel: 'border-teal-200' },
	{ id: 'orange', swatch: 'bg-orange-500', widget: 'bg-orange-600', panel: 'border-orange-200' },
] as const

export function CustomizeBenefitAnimation() {
	const { t } = useTranslation()
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const { ref, inView } = useInView()
	const step = useLoop(3, 1800, inView, reducedMotion)
	const theme = THEMES[step]

	return (
		<div ref={ref}>
			<AnimFrame>
				<div className="mb-4 flex items-center gap-3">
					<Palette className="h-4 w-4 text-muted-foreground" />
					<div className="flex gap-2">
						{THEMES.map((item, index) => (
							<span
								key={item.id}
								className={cn(
									'h-6 w-6 rounded-full ring-2 ring-offset-2 transition-all',
									item.swatch,
									index === step ? 'ring-gray-900' : 'ring-transparent'
								)}
							/>
						))}
					</div>
				</div>
				<div className={cn('rounded-xl border bg-white p-4 shadow-sm transition-colors', theme.panel)}>
					<p className="text-sm font-semibold">{t('featuresAlternating.anim.embed.widgetGreeting')}</p>
					<div className="mt-3 space-y-2">
						<div className="flex items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-sm">
							<Bug className="h-4 w-4 text-red-500" />
							{t('featuresAlternating.anim.reportBug')}
						</div>
						<div className="flex items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-sm">
							<Lightbulb className="h-4 w-4 text-purple-500" />
							{t('featuresAlternating.anim.requestFeature')}
						</div>
					</div>
					<div className="mt-4 flex justify-end">
						<span
							className={cn(
								'rounded-full px-3 py-1.5 text-xs font-semibold text-white transition-colors',
								theme.widget
							)}
						>
							{t('featuresAlternating.anim.sendReply')}
						</span>
					</div>
				</div>
			</AnimFrame>
		</div>
	)
}
